import { ApiResponseObject } from '@interfaces/api-response/1.0';
import { FulfillmentInfo, OrderProcessingStatus, TipDetail } from '@generated/tcea/models';
import { OrderFulfillmentInfo } from '@generated/tcea/models/order-fulfillment-info';
import { PaymentDetails } from '@generated/tcea/models/payment-details';

export interface Orders {
  orderId: string;
  orderDisplayId?: string;
  orderDate: Date | string;
  readyDate?: Date | string;
  scheduledOrderDate?: Date | string;
  channelName: string;
  paymentType: number | string;
  failureReason: string;
  currentOrderState: string;
  customerName: string;
  storeId: string;
  externalId: string;
  channelTotal: string;
  customerEmail: string;
  phone: string;
  storeNumber: number;
  checkId: string;
  channelNameDescription: string;
  serviceTypeDescription?: string;
  paymentTypeDescription: string;
  failureReasonDescription: string;
  action?: boolean;
  delayedOrder?: boolean;
  serviceType?: string;
  isCancelable?: boolean;
  orderProcessingStatus?: string;
  cancellationReason?: string;
  aggregatorStatus?: string;
}

export interface OrderSummaries {
  tenantId: string;
  orderId: string;
  orderDate: Date | string;
  localOrderDate?: Date | string;
  /** @deprecated Use fulfillmentInfo.fulfillmentDateTime or fulfillmentInfo.estimatedTime */
  estimatedTime?: Date | string;
  channelName: string;
  subChannel?: string;
  channelTotal: string;
  tip?: string;
  channelNameDescription: string;
  /** @deprecated Use fulfillmentInfo.serviceType */
  serviceType?: string;
  /** @deprecated Use fulfillmentInfo.serviceTypeDescription */
  serviceTypeDescription?: string;
  fulfillmentInfo?: OrderFulfillmentInfo;
  paymentType: number | string;
  paymentTypeDescription: string;
  failureReason?: string;
  failureReasonDescription?: string;
  orderProcessingStatus?: string;
  user: {
    address: {
      address1: string;
      address2: string;
      city: string;
      postcode: string;
      state: string;
    };
    firstName: string;
    lastName: string;
    userId: string;
    phone: string;
    email: string;
  };
  store: {
    name: string;
    number: number;
    storeId: string;
    address: {
      address1: string;
      address2: string;
      city: string;
      postcode: string;
      state: string;
    };
  };
  checkId: string;
  externalId: string;
  orderDisplayId: string;
  hasRefunds?: boolean;
  isCancelable?: boolean;
  delayedOrder?: boolean;
  itemCount?: number;
  cancellationReason?: string;
  aggregatorStatus?: string;
}

export interface OrderSummariesResponse extends ApiResponseObject {
  _embedded: {
    orderSummaries: OrderSummaries[];
  };
}

export interface DeliveryDetails {
  jobStatus: string;
  friendlyJobStatus: string;
  driverName: string;
  pickupEta: string;
  dropoffEta: string;
  longitude: number;
  latitude: number;
  trackingUrl: string;
  deliveryProvider: string;
  vehicleType: string;
  vehicleName: string;
  cancelReason: string;
  message: string;
  customerOrderId: string;
  tceOrderId: string;
  country: string;
  jobId: string;
}

export interface OrderMetaData {
  deviceType?:
    | 'ios'
    | 'android'
    | 'mobileOther'
    | 'windows'
    | 'mac'
    | 'linux'
    | 'chromebook'
    | 'desktopOther'
    | 'other';
  orderingChannel?:
    | 'web'
    | 'mobile'
    | 'call_center'
    | 'kiosk'
    | 'indoor_digital_menu_board'
    | 'outdoor_digital_menu_board'
    | 'chat';
}

export interface OrderDetails extends OrderSummaries {
  customerStatus?: string;
  orderInstructions?: string;
  deliveryInstructions: string;
  deliveryDetails?: DeliveryDetails;
  hasRefunds: boolean;
  isCancelable: boolean;
  items: Item[];
  refunds?: OrderViewRefund[];
  paymentAttempts?: any[];
  paymentDetails?: PaymentDetails;
  subtotal: number;
  tax: number;
  taxPayerInfo?: TaxPayerInfo;
  cancellationReason?: string;
  friendlyCancellationReason?: string;
  omOrderStatus?: string;
  fulfillmentInfo?: FulfillmentInfo;
  scheduledFor?: string;
  orderProcessingStatus?: OrderProcessingStatus;
  metaData?: OrderMetaData;
  tips?: TipDetail[];
}

export interface Item {
  description: string;
  elementType: string;
  instructions: string;
  price: number;
  quantity: number;
  guestName?: string;
  items?: Item[];
}
export interface OrderViewRefund {
  id?: string;
  amount?: number;
  currency?: string;
  reason?: string;
  description?: string;
  status?: string;
  date?: string;
  timestamp?: string;
  transactionId?: string;
}

export enum PaymentType {
  Cash = 0,
  Credit = 1,
}

export interface CheckoutCancelStatus {
  tenantId?: string;
  orderId?: string;
  cancellationRequested?: boolean;
  isCancelled?: boolean;
  created?: string;
  lastUpdated?: string;
  cancellationRequestedTime?: string;
  cancelledTime?: string;
  correlationId?: string;
}

export interface OrderRefunds {
  transactionId?: string;
  tenantId?: string;
  storeId?: string;
  orderId?: string;
  amount?: number;
  currency?: string;
  date?: string;
  timestamp?: string;
  reason?: string;
  description?: string;
  instanceId?: string;
  status?: string;
}

export interface TaxPayerInfo {
  documentNumber?: string;
  documentType?: string;
  name?: string;
  address?: string;
}

export enum CustomerFacingReasons {
  'CANCELLED_BY_AGGREGATOR' = 'cancelledByAggregator',
  'CHANGED_MY_MIND' = 'changedMyMind',
  'ADD_OR_REMOVE_ITEMS' = 'addOrRemoveItems',
  'CHANGE_DELIVERY_TIME' = 'changeDeliveryTime',
  'CHANGE_SERVICE_TYPE' = 'changeServiceType',
  'CUSTOMER_REQUEST' = 'customerRequest',
  'IMPOSSIBLE_TO_FULFILL_ORDER' = 'impossibleToFulfilOrder',
  'STORE_CLOSED' = 'storeClosed',
  'TEST_ORDER' = 'testOrder',
}

export enum OrderCancellationReason {
  STORE_CLOSED = 'STORE_CLOSED',
  KITCHEN_AT_CAPACITY = 'KITCHEN_AT_CAPACITY',
  CUSTOMER_REQUEST = 'CUSTOMER_REQUEST',
  DRIVER_DID_NOT_SHOW = 'DRIVER_DID_NOT_SHOW',
  ITEMS_UNAVAILABLE = 'ITEMS_UNAVAILABLE',
  TEST_ORDER = 'TEST_ORDER',
}

export interface OrderDashboardItem extends OrderSummaries {
  pickupTimeCountdown?: string;
  timerColor?: string;
}
